import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useFormContext } from '@/lib/form-contexts'
import { getErrorMessage, isZodError } from '@/lib/utils'
import { AlertCircle } from 'lucide-react'

// A form-level schema reports its issues keyed by field path, and those fields show the same
// messages inline. The summary repeats them plainly instead of presenting them as an error.
const isFieldIssueMap = (error: unknown): error is Record<string, unknown[]> =>
  error !== null &&
  typeof error === 'object' &&
  !Array.isArray(error) &&
  Object.values(error).every((issues) => Array.isArray(issues) && issues.every(isZodError))

const errorMessages = (errors: readonly unknown[]) => [
  ...new Set(
    errors
      .flatMap((error) => (isFieldIssueMap(error) ? Object.values(error).flat() : [error]))
      .map(getErrorMessage)
      .filter(
        (message): message is string => typeof message === 'string' && message.trim().length > 0
      )
  ),
]

export type FormErrorProps = {
  title?: string
}

export default function FormError({ title }: FormErrorProps) {
  const form = useFormContext()

  return (
    <form.Subscribe selector={(state): unknown[] => state.errors}>
      {(errors) => {
        const messages = errorMessages(errors)
        if (messages.length === 0) return null
        const heading = title ?? (errors.every(isFieldIssueMap) ? null : 'Error')

        return (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            {heading ? <AlertTitle>{heading}</AlertTitle> : null}
            <AlertDescription>
              {messages.length === 1 ? (
                messages[0]
              ) : (
                <ul className="list-disc space-y-1 pl-4">
                  {messages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )
      }}
    </form.Subscribe>
  )
}
